import {FC, useCallback, useLayoutEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import styled from "@emotion/styled";
import {partial} from "lodash";
import Slider, {CustomArrowProps, Settings} from "react-slick";
import {useTranslation} from "react-i18next";
import {PureButton, SecondaryLink} from "components/Common";
import {FanVotePlayer} from "components/FanVotePlayer";
import {Dots} from "components/UserTeamVotes/TeamView/Dots";
import {Exist} from "components";
import {getFanVotePlayers, getIsAllTeamPicked} from "modules/selectors";
import {useIsMobile} from "modules/utils";
import {breakpoints} from "assets/theme";
import {ReactComponent as LeftArrow} from "assets/images/icons/sliderLeftArrow.svg";
import {ReactComponent as RightArrow} from "assets/images/icons/sliderRightArrow.svg";
import mobileFieldImage from "assets/images/fanVote/mobileField.svg";
import fieldImage from "assets/images/fanVote/field.svg";
import pageMobileBg from "assets/images/pageMobileBg.png";
import {useLocation} from "react-router-dom";

const Card = styled.div`
	width: 100%;
	position: relative;
	min-height: 320px;
	background: url(${mobileFieldImage}) no-repeat center #010101;
	background-size: cover;
	border-radius: 4px;
	border: 2px solid ${({theme}) => theme.colorPalette.secondary};
	background-position-y: 20%;

	@media (min-width: ${breakpoints.tablet}) {
		background-position-y: center;
		background-image: url(${fieldImage});
		margin: 0 20px;
	}

	@media (min-width: ${breakpoints.tablet}) {
		min-height: 737px;
	}
`;

const Title = styled.div`
	display: flex;
	justify-content: center;
	font-size: 28px;
	font-weight: 700;
	line-height: 120%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	color: ${({theme}) => theme.colorPalette.white};
	margin-bottom: 20px;

	@media (min-width: ${breakpoints.tablet}) {
		justify-content: space-between;
		font-size: 32px;
		margin-bottom: 46px;
	}
`;

const Content = styled.div`
	@media (min-width: ${breakpoints.tablet}) {
		position: absolute;
		left: 0;
		right: 0;
		top: 50%;
		transform: translateY(-50%);
	}
`;

const Arrow = styled(PureButton)`
	padding: 8px;

	&:disabled {
		opacity: 0.3;
	}
`;

const ItemContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const DotsWrapper = styled.div`
	margin-top: 26px;
	margin-bottom: 32px;
`;

const MobileContainer = styled.div`
	width: 100%;
	background: url(${pageMobileBg}) no-repeat center #010101;
	background-size: 100% 100%;
	padding: 60px 20px 0;
`;

const Wrapper = styled.div`
	width: 100%;
`;

const DesktopContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	width: 100%;

	.slick-slider.slick-initialized {
		display: flex;
		align-items: center;
	}

	.slick-list {
		width: 100%;
	}
`;

const Link = styled(SecondaryLink)`
	display: block;
	text-align: center;
	margin-bottom: 40px;

	@media (min-width: ${breakpoints.tablet}) {
		margin-bottom: 0;
	}
`;

const NextArrow: FC<CustomArrowProps> = (props) => {
	const {onClick, currentSlide = 0, slideCount} = props;
	const isDisabled = currentSlide + 1 === slideCount;

	return (
		<Arrow onClick={onClick} disabled={isDisabled}>
			<RightArrow />
		</Arrow>
	);
};

const PrevArrow: FC<CustomArrowProps> = (props) => {
	const {onClick, currentSlide} = props;
	const isDisabled = !currentSlide;

	return (
		<Arrow onClick={onClick} disabled={isDisabled}>
			<LeftArrow />
		</Arrow>
	);
};

const getUri = (search: string, id: number) => {
	if (search) {
		return `/picks${search}&id=${id}`;
	}
	return `/picks?id=${id}`;
};

export const UserFanVotes: FC = () => {
	const {t} = useTranslation();
	const {search} = useLocation();
	const [index, setIndex] = useState(0);
	const fanVotePlayers = useSelector(getFanVotePlayers);
	const isAllTeamPicked = useSelector(getIsAllTeamPicked);
	const sliderRef = useRef<Slider>(null);
	const {isMobile} = useIsMobile();
	const player = fanVotePlayers[index];

	const settings: Settings = {
		arrows: false,
		dots: false,
		speed: 500,
		infinite: false,
		slidesToShow: 1,
		slidesToScroll: 1,
		beforeChange: (current, next) => setIndex(next),
	};

	const arrowProps: CustomArrowProps = {
		slideCount: fanVotePlayers.length,
		currentSlide: index,
	};

	const changeHandler = useCallback((index: number) => {
		sliderRef.current?.slickGoTo(index);
	}, []);

	useLayoutEffect(() => {
		if (isMobile) {
			setIndex(0);
		}
	}, [isMobile, setIndex]);

	if (isMobile) {
		return (
			<Wrapper>
				<MobileContainer>
					<Title>
						{t("page.vote.history.vote")} {index + 1}
					</Title>
					<Card>
						<Slider ref={sliderRef} {...settings}>
							{fanVotePlayers.map((player) => (
								<ItemContainer key={player.id}>
									<FanVotePlayer player={player} />
								</ItemContainer>
							))}
						</Slider>
					</Card>

					<DotsWrapper>
						<Dots
							length={fanVotePlayers.length}
							index={index}
							changeHandler={changeHandler}
						/>
					</DotsWrapper>

					<Exist when={!isAllTeamPicked}>
						<Link to={getUri(search, player.id)}>{t("page.vote.history.add")}</Link>
					</Exist>
				</MobileContainer>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Title>
				<div>
					{t("page.vote.history.vote")} {index + 1}
				</div>

				<Exist when={!isAllTeamPicked}>
					<SecondaryLink to={getUri(search, player.id)}>
						{t("page.vote.history.add")}
					</SecondaryLink>
				</Exist>
			</Title>

			<DesktopContainer>
				<PrevArrow {...arrowProps} onClick={partial(changeHandler, index - 1)} />

				<Card>
					<Content>
						<Slider ref={sliderRef} {...settings}>
							{fanVotePlayers.map((player) => (
								<ItemContainer key={player.id}>
									<FanVotePlayer player={player} />
								</ItemContainer>
							))}
						</Slider>
					</Content>
				</Card>

				<NextArrow {...arrowProps} onClick={partial(changeHandler, index + 1)} />
			</DesktopContainer>
		</Wrapper>
	);
};
